import { StateCreator } from 'zustand';
import { AuthSlice, KharonStore, GoogleUser } from '../types';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail,
  signOut,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  deleteUser,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendEmailVerification
} from 'firebase/auth';
import { auth, db } from '../../lib/firebase';
import { doc, getDoc, setDoc, deleteDoc, writeBatch, collection } from 'firebase/firestore';
import { getDefaultTemplates } from '../../lib/defaultTemplates';

export const createAuthSlice: StateCreator<
  KharonStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AuthSlice
> = (set, get) => ({
  currentUser: null,
  token: null,

  setCurrentUser: (user) =>
    set((state) => {
      state.currentUser = user;
    }, false, 'auth/setCurrentUser'),
    
  setToken: (token) =>
    set((state) => {
      state.token = token;
    }, false, 'auth/setToken'),

  login: async ({ email, password, rememberMe = true }) => {
    set((state) => { state.setIsLoading(true); state.setError(null); }, false, 'auth/login/start');
    try {
      if (!password) throw new Error("Contraseña requerida");
      
      const persistenceType = rememberMe ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(auth, persistenceType);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Removed hard signOut for unverified emails to allow audit flow
      if (!user.emailVerified) {
         console.warn("User email not verified, but allowing entry for audit/onboarding.");
      }
      
      const userDoc = await getDoc(doc(db, "users", user.uid));
      let funeralHomeId = 'default-fh';
      let role = 'user';
      let title = 'Asesor Funerario';

      if (userDoc.exists()) {
        const userData = userDoc.data();
        funeralHomeId = userData.funeralHomeId || 'default-fh';
        role = userData.role || 'user';
        title = userData.title || 'Asesor Funerario';
      } else {
        await setDoc(doc(db, "users", user.uid), {
          id: user.uid,
          name: user.displayName || 'Usuario KHARON',
          email: user.email || '',
          funeralHomeId: 'default-fh',
          role: 'user',
          title: 'Asesor Funerario',
          subscriptionActive: false,
          planType: 'none'
        });
      }

      const kharonUser = {
        id: user.uid,
        name: user.displayName || 'Usuario KHARON',
        email: user.email || '',
        avatar: user.photoURL || '',
        role: role as 'admin' | 'user',
        title: title,
        funeralHomeId: funeralHomeId,
        onboarding: (userDoc.exists() ? userDoc.data().onboarding : undefined),
        subscriptionActive: userDoc.exists() ? userDoc.data().subscriptionActive : false,
        planType: userDoc.exists() ? userDoc.data().planType : 'none'
      };

      const token = await user.getIdToken();
      
      set((state) => {
        state.currentUser = kharonUser as any;
        state.token = token;
        state.setIsLoading(false);
      }, false, 'auth/login/success');
    } catch (error: any) {
      const errorCode = error.code || error.message;
      set((state: any) => {
        state.error = errorCode;
        state.isLoading = false;
      }, false, 'auth/login/error');
      throw error;
    }
  },
  
  register: async ({ name, email, password }) => {
    set((state) => { state.setIsLoading(true); state.setError(null); }, false, 'auth/register/start');
    try {
      if (!password) throw new Error("Contraseña requerida");
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await updateProfile(user, { displayName: name });
      await sendEmailVerification(user);

      const funeralHomeId = `fh-${Date.now()}`;

      const kharonUser = {
        id: user.uid,
        name: name,
        email: user.email || '',
        avatar: '',
        role: 'admin' as const,
        title: 'Gerente Funerario',
        funeralHomeId: funeralHomeId,
        onboarding: {
          completed: false,
          steps: {
            profile: false,
            catalog: false,
            templates: false
          }
        },
        subscriptionActive: false,
        planType: 'none',
        trialStartDate: new Date().toISOString()
      };

      await setDoc(doc(db, "users", user.uid), kharonUser);

      await setDoc(doc(db, "settings", funeralHomeId), {
        name: "Nueva Funeraria",
        taxId: "",
        address: "",
        phone: "",
        email: email,
        website: "",
        funeralHomeId: funeralHomeId
      });

      // Generar plantillas por defecto
      const batch = writeBatch(db);
      const templates = getDefaultTemplates(funeralHomeId);
      
      templates.forEach(template => {
        const templateRef = doc(db, "templates", template.id);
        batch.set(templateRef, template);
      });
      
      await batch.commit();

      // NO seteamos session aquí, le forzamos auth check via email.
      await signOut(auth);

      set((state) => {
        state.setIsLoading(false);
      }, false, 'auth/register/success');
    } catch (error: any) {
      const errorCode = error.code || error.message;
      set((state: any) => {
        state.error = errorCode;
        state.isLoading = false;
      }, false, 'auth/register/error');
      throw error;
    }
  },
  
  loginWithGoogle: async () => {
    set((state) => { state.setIsLoading(true); state.setError(null); }, false, 'auth/googleLogin/start');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userDoc = await getDoc(doc(db, "users", user.uid));
      let kharonUser: any = {
        id: user.uid,
        name: user.displayName || 'Usuario Google',
        email: user.email || '',
        avatar: user.photoURL || ''
      };

      if (userDoc.exists()) {
        const userData = userDoc.data();
        kharonUser = {
          ...kharonUser,
          funeralHomeId: userData.funeralHomeId || 'default-fh',
          role: userData.role || 'user',
          title: userData.title || 'Asesor Funerario',
          onboarding: userData.onboarding
        };
      } else {
        const funeralHomeId = `fh-google-${Date.now()}`;
        const newUserProfile = {
          ...kharonUser,
          funeralHomeId: funeralHomeId,
          role: 'admin',
          title: 'Gerente Funeraria',
          onboarding: {
            completed: false,
            steps: { profile: false, catalog: false, templates: false }
          },
          subscriptionActive: false,
          planType: 'none',
          trialStartDate: new Date().toISOString()
        };
        await setDoc(doc(db, "users", user.uid), newUserProfile);
        
        await setDoc(doc(db, "settings", funeralHomeId), {
          name: "Nueva Funeraria (Google)",
          taxId: "",
          address: "",
          phone: "",
          email: user.email,
          website: "",
          funeralHomeId: funeralHomeId
        });

        // Generar plantillas por defecto
        const batch = writeBatch(db);
        const templates = getDefaultTemplates(funeralHomeId);
        
        templates.forEach(template => {
          const templateRef = doc(db, "templates", template.id);
          batch.set(templateRef, template);
        });
        
        await batch.commit();

        kharonUser = newUserProfile;
      }

      const token = await user.getIdToken();

      set((state) => {
        state.currentUser = kharonUser;
        state.token = token;
        state.setIsLoading(false);
      }, false, 'auth/googleLogin/success');
      
    } catch (error: any) {
      const errorCode = error.code || error.message;
      set((state: any) => {
        state.error = errorCode;
        state.isLoading = false;
      }, false, 'auth/googleLogin/error');
    }
  },

  forgotPassword: async (email) => {
    set((state) => { state.setIsLoading(true); state.setError(null); }, false, 'auth/forgotPassword/start');
    try {
      await sendPasswordResetEmail(auth, email);
      set((state) => { state.setIsLoading(false); }, false, 'auth/forgotPassword/success');
    } catch (error: any) {
      set((state) => { state.setError(error.message); state.setIsLoading(false); }, false, 'auth/forgotPassword/error');
      throw error;
    }
  },

  resetPassword: async (token, newPassword) => {
    set((state) => { state.setIsLoading(true); }, false, 'auth/resetPassword/start');
    set((state) => { state.setIsLoading(false); }, false, 'auth/resetPassword/complete');
  },

  verifyEmail: async (token) => {
    set((state) => { state.setIsLoading(true); }, false, 'auth/verifyEmail/start');
    set((state) => { state.setIsLoading(false); }, false, 'auth/verifyEmail/complete');
  },
  
  deleteFuneralHomeAccount: async (password?: string) => {
    set((state) => { state.setIsLoading(true); state.setError(null); }, false, 'auth/deleteAccount/start');
    try {
      const user = auth.currentUser;
      const state = get();
      const currentUser = state.currentUser;

      if (!user) throw new Error("No hay usuario autenticado.");
      if (currentUser?.role !== 'admin') throw new Error("Solo el administrador puede eliminar la cuenta.");

      // Re-authentication
      if (password && user.email) {
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
      } else if (!password && user.providerData.some(p => p.providerId === 'password')) {
         throw new Error("Se requiere la contraseña para cuentas de email.");
      }

      const uid = user.uid;
      const funeralHomeId = currentUser.funeralHomeId;

      // Delete user document
      await deleteDoc(doc(db, "users", uid));

      // Delete funeral home settings
      if (funeralHomeId && funeralHomeId !== 'default-fh') {
        await deleteDoc(doc(db, "settings", funeralHomeId));
      }

      // Delete Firebase Auth user
      await deleteUser(user);

      // Clear state
      set((state) => {
        state.currentUser = null;
        state.token = null;
        state.setIsLoading(false);
      }, false, 'auth/deleteAccount/success');

    } catch (error: any) {
      set((state) => {
        state.setError(error.message || 'Error al eliminar la cuenta.');
        state.setIsLoading(false);
      }, false, 'auth/deleteAccount/error');
      throw error;
    }
  },

  logout: () => {
    signOut(auth);
    set((state) => {
      state.currentUser = null;
      state.token = null;
    }, false, 'auth/logout');
  },

  initializeAuth: (onReady) => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        let kharonUser: any = {
          id: user.uid,
          name: user.displayName || 'Usuario KHARON',
          email: user.email || '',
          avatar: user.photoURL || '',
          role: 'user',
          title: 'Asesor Funerario',
          funeralHomeId: 'default-fh'
        };

        if (userDoc.exists()) {
          const userData = userDoc.data();
          kharonUser = {
            ...kharonUser,
            role: userData.role || 'user',
            title: userData.title || 'Asesor Funerario',
            funeralHomeId: userData.funeralHomeId || 'default-fh',
            onboarding: userData.onboarding,
            subscriptionActive: userData.subscriptionActive || false,
            planType: userData.planType || 'none'
          };
        }

        const token = await user.getIdToken();
        set((state) => {
          state.currentUser = kharonUser;
          state.token = token;
        }, false, 'auth/initialize/success');
      } else {
        set((state) => {
          state.currentUser = null;
          state.token = null;
        }, false, 'auth/initialize/empty');
      }
      if (onReady) onReady();
    });
  },
});
